import { useState, useEffect, useCallback } from 'react'
import { api } from '../../api/client'

// Real backing store for the Lead and Deal detail modals' "Todo List"
// widget - see packages/db/src/goodday/Todo.ts for why this replaced
// local-only React state seeded with hardcoded fake items on every
// lead/deal. Mirrors useLeadActivity.ts's shape for the same
// resource-under-an-entity pattern; one hook covers both entity types since
// only the URL prefix differs.

export interface Todo {
  id: string
  text: string
  done: boolean
  createdAt: string
}

export interface CreateTodoInput {
  text: string
}

function fromTodoRow(row: any): Todo {
  return {
    id: row._id ?? row.id,
    text: row.text,
    done: !!row.done,
    createdAt: row.createdAt,
  }
}

export function useTodos(entityType: 'lead' | 'deal', entityId: string | undefined) {
  const [todos, setTodos] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  const basePath = `/${entityType}s/${entityId}/todos`

  const fetchTodos = useCallback(async () => {
    if (!entityId) {
      setTodos([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<any>(basePath)
      setTodos((res?.data || []).map(fromTodoRow))
    } catch (err) {
      console.error(`Failed to load ${entityType} todos`, err)
      setTodos([])
    } finally {
      setLoading(false)
    }
  }, [basePath, entityId, entityType])

  useEffect(() => {
    fetchTodos()
  }, [fetchTodos])

  const createTodo = useCallback(
    async (input: CreateTodoInput) => {
      if (!entityId) throw new Error(`${entityType}Id is required`)
      const res = await api.post<any>(basePath, input, { skipErrorToast: true })
      const created = fromTodoRow(res?.data)
      setTodos((prev) => [...prev, created])
      return created
    },
    [basePath, entityId, entityType]
  )

  const toggleTodo = useCallback(
    async (id: string, done: boolean) => {
      if (!entityId) throw new Error(`${entityType}Id is required`)
      setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, done } : t)))
      try {
        await api.patch<any>(`${basePath}/${id}`, { done }, { skipErrorToast: true })
      } catch (err) {
        setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, done: !done } : t)))
        throw err
      }
    },
    [basePath, entityId, entityType]
  )

  const deleteTodo = useCallback(
    async (id: string) => {
      if (!entityId) throw new Error(`${entityType}Id is required`)
      await api.delete<any>(`${basePath}/${id}`, { skipErrorToast: true })
      setTodos((prev) => prev.filter((t) => t.id !== id))
    },
    [basePath, entityId, entityType]
  )

  return { todos, loading, createTodo, toggleTodo, deleteTodo, refetch: fetchTodos }
}

export function useLeadTodos(leadId: string | undefined) {
  return useTodos('lead', leadId)
}

export function useDealTodos(dealId: string | undefined) {
  return useTodos('deal', dealId)
}
